import { ref, shallowRef, computed, watch, watchEffect, toValue } from 'vue'
import type { Ref, MaybeRefOrGetter } from 'vue'
import { useDebounceFn } from '@vueuse/core'

import { SEARCH_DEBOUNCE_MS, SELECT_PAGE_LIMIT } from '@/shared/constants'
import type { EntityId } from './types'

type UseListOptions = {
  enabled?: MaybeRefOrGetter<boolean>
}

type UseListFn<TParams, TItem> = (
  params: MaybeRefOrGetter<TParams>,
  listOptions?: UseListOptions
) => {
  items: Ref<TItem[]>
  /**
   * How many rows match, out of which this is one page — `createEntityQueries` reads it
   * off `meta.total`. Optional because a hand-written list hook need not have one; the
   * field then says nothing about a total rather than claiming zero.
   */
  total?: Ref<number>
  isLoading: Ref<boolean>
  isFetching?: Ref<boolean>
  /**
   * Whether the query has ever settled — TanStack reports it, a hand-written hook need
   * not. It is the only thing that tells "not fetching because it is done" apart from
   * "not fetching yet", which is what `hasLoaded` below is built on.
   */
  isFetched?: Ref<boolean>
  isError?: Ref<boolean>
  error?: Ref<unknown>
  refetch: () => void
}

/**
 * The paging and searching behind a search-backed select: it takes an entity's list hook
 * and returns the growing option list, a debounced `onSearch`, and `loadMore`.
 *
 * An entity's `<Entity>Select.vue` does not call this — it calls `useEntitySelect`, which
 * is this plus the seeding, pinning and filter-reset a form field needs. Reach for
 * `useRemoteSelect` directly only over a list hook that is not an entity's. A select over
 * a catalog fetched whole (`CountrySelect`) needs neither and takes `Select`.
 *
 * Pages accumulate rather than replace: only page 1 resets the list, later pages are
 * appended by id. Anything that changes what is being listed — a new search, a new filter
 * — must go through `onSearch` or `resetPaging`, or page 2 of the new query lands
 * underneath page 1 of the old one.
 *
 * The list hook may be hand-written; `total`, `isFetching`, `isFetched`, `isError` and
 * `error` are all optional, and their absence is read as "unknown", never as zero or false.
 */
export function useRemoteSelect<TItem, TParams>(
  useList: UseListFn<TParams, TItem>,
  options: {
    /**
     * One option out of one record. The value is an `EntityId` and not `any`, so the type
     * survives into the options the field renders.
     */
    mapOption: (item: TItem) => {
      label: string
      value: EntityId
      status?: boolean | null
      statusText?: string
    }
    getId?: (item: TItem) => string | number
    limit?: number
    enabled?: MaybeRefOrGetter<boolean>
    /**
     * How many rows the endpoint pins to every page *on top of* `limit` — the `selected`
     * ids a field seeded with a value asks it to keep listed. It has to be told, because
     * `buildParams` is the only thing that knows the request carries any.
     */
    pinnedCount?: MaybeRefOrGetter<number>
    buildParams: (ctx: {
      query?: string
      page: number
      limit: number
    }) => TParams
  }
) {
  const search = ref('')
  // Pages, not an offset: the list endpoints take `page` and silently drop anything else,
  // so an offset asked for the first page over and over.
  const page = ref(1)
  const limit = options.limit ?? SELECT_PAGE_LIMIT

  // Rows are always replaced wholesale, never mutated in place, so a shallow ref is
  // both correct and free of the `UnwrapRefSimple<TItem>` mangling a deep `ref` adds.
  const allItems = shallowRef<TItem[]>([])
  const hasMore = ref(true)

  const params = computed(() =>
    options.buildParams({
      query: search.value || undefined,
      page: page.value,
      limit
    })
  )

  const query = useList(params, { enabled: options.enabled })
  const isFetching = query.isFetching ?? query.isLoading

  // The fallback for a record that carries the ordinary `id`. Narrowed to a shape rather
  // than cast through `any`: `{ id?: EntityId }` says what is being assumed, and a record
  // whose id is something else still has to pass `getId` explicitly.
  const getId =
    options.getId ?? ((item: TItem) => (item as { id?: EntityId }).id)

  /**
   * The size of the list being paged through, counted by the endpoint under the same
   * search and filter — so it moves with the query rather than describing the catalog.
   * `null` when the hook reports none, which reads differently from none matching.
   *
   * The pinned rows are added back because the count arrives without them: they are set aside
   * before the rest is counted and appended to the page afterwards. Both the caption the field
   * shows and `hasMore` below want the number of rows the dropdown can end up holding.
   *
   * Declared above `syncItems` because that reads it, and the watcher below runs
   * immediately — a `const` further down would still be in its dead zone by then.
   */
  const total = computed(() => {
    const reported = query.total?.value

    if (reported == null) return null

    return reported + (toValue(options.pinnedCount) ?? 0)
  })

  const syncItems = () => {
    const newItems = query.items.value ?? []
    const loadedBefore = allItems.value.length

    if (page.value === 1) {
      allItems.value = newItems
    } else {
      // Appended by id rather than concatenated: a page that arrives a second time —
      // a background refetch of a key already loaded — would otherwise list every one
      // of its rows twice.
      const seen = new Set(
        allItems.value
          .map(getId)
          .filter((id) => id != null)
          .map(String)
      )

      allItems.value = [
        ...allItems.value,
        ...newItems.filter((item) => {
          const id = getId(item)

          // A row with no id is kept rather than deduplicated: `String(undefined)` is
          // `'undefined'` for every one of them, so they would all collapse into the first.
          // `itemsById` below skips them for the same reason.
          return id == null || !seen.has(String(id))
        })
      ]
    }

    if (page.value > 1 && allItems.value.length === loadedBefore) {
      // A page that added nothing is past the end. That is also what a `selected` id with
      // no record behind it looks like: `total` counts a row the list can never load, so
      // the comparison below would never come true.
      hasMore.value = false
      return
    }

    hasMore.value =
      total.value === null
        ? // A full page probably has another behind it. The guess is wrong for every
          // request carrying `selected`: those come back with `limit` rows *plus* the
          // pinned ones, so a plain equality never holds and a seeded field stops at
          // page one.
          newItems.length === limit
        : allItems.value.length < total.value
  }

  watch(
    () => query.items.value,
    () => {
      syncItems()
    },
    { immediate: true }
  )

  /**
   * Paging belongs to one list: a different query, or a different narrowing, starts over
   * from the first page with `hasMore` unknown again.
   */
  function resetPaging() {
    page.value = 1
    hasMore.value = true
  }

  const onSearch = useDebounceFn((q: string) => {
    // Nothing to reset, and resetting anyway breaks the field: clearing a value calls this
    // with the query it already has, so no params change, so no refetch follows to correct
    // `hasMore` — and the dropdown goes back to believing in a page 2 the first page
    // ruled out.
    if (q === search.value && page.value === 1) return

    search.value = q
    resetPaging()
  }, SEARCH_DEBOUNCE_MS)

  const loadMore = () => {
    if (!hasMore.value || isFetching.value) return
    page.value += 1
  }

  /**
   * Whether this field has ever been filled — sticky, and never reset.
   *
   * `isFetching` alone cannot answer it: a disabled query and a finished one both report
   * `false`, so a caller wanting to know "is the list still on its way?" reads the same
   * value before the first request as after the last one. TanStack's `isFetched` tells
   * them apart per key; a hook without one falls back to "not fetching", which is right
   * from the moment the first request ends and merely unhelpful before it.
   *
   * Per *key* is why it is latched rather than passed through: a search or a second page
   * makes a new key, whose `isFetched` is false again, and the field must not fall back
   * into its first-load state every time the user types.
   */
  const hasLoaded = ref(false)

  watchEffect(() => {
    const settled = query.isFetched ? query.isFetched.value : !isFetching.value

    if (settled) hasLoaded.value = true
  })

  const mappedOptions = computed(() => allItems.value.map(options.mapOption))

  const itemsById = computed(() => {
    const map = new Map<string, TItem>()

    allItems.value.forEach((item) => {
      const id = getId(item)

      if (id != null) {
        map.set(String(id), item)
      }
    })

    return map
  })

  const getById = (id: string | number | null | undefined) => {
    if (id == null) return null
    return itemsById.value.get(String(id)) ?? null
  }

  return {
    options: mappedOptions,
    total,
    // Only the first page loads *the field*; a page appended underneath must not put the
    // select into its loading state, which swaps the list out for a spinner and reads as
    // the dropdown flashing.
    isLoading: computed(() => isFetching.value && page.value === 1),
    hasLoaded,
    isError: computed(() => !!query.isError?.value),
    error: computed(() => query.error?.value),
    retry: () => query.refetch(),
    onSearch,
    loadMore,
    resetPaging,
    rawItems: allItems,
    itemsById,
    getById
  }
}
